import type { TrialStatus } from '$lib/types/ai';

/**
 * Trial Limits Constants
 */
export const TRIAL_LIMITS = {
  DURATION_DAYS: 14,
  TRIAL_UNLIMITED: true,
} as const;

export const FREE_TIER_LIMITS = {
  DAILY_SPELL_CHECK_LIMIT: 10,
  DAILY_AI_ANALYSIS_LIMIT: 10,
  RESET_HOUR: 0,
} as const;

/**
 * Trial Management Service for Svelte Application
 * Adapted from reference/core/services/TrialService.ts
 * Manages AI feature trial period and free tier transitions
 */
export class TrialService {
  
  /**
   * Initialize trial on first installation
   */
  static initializeTrial(): TrialStatus {
    const now = new Date();
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + TRIAL_LIMITS.DURATION_DAYS);
    
    return {
      isActive: true,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      daysRemaining: TRIAL_LIMITS.DURATION_DAYS,
      hasExpired: false,
      isFreeTier: false,
      // Unlimited during trial period like premium
      aiUsageCount: 0,
      aiDailyLimit: Infinity,
      spellCheckUsageCount: 0,
      spellCheckDailyLimit: Infinity
    };
  }
  
  /**
   * Check and update trial status
   */
  static checkTrialStatus(trial?: TrialStatus): TrialStatus {
    // Initialize new trial if none exists
    if (!trial) {
      return this.initializeTrial();
    }
    
    // Already converted to free tier
    if (trial.isFreeTier) {
      return this.checkFreeTierUsage(trial);
    }
    
    // Check trial period
    if (trial.endDate) {
      const now = new Date();
      const endDate = new Date(trial.endDate);
      const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysRemaining <= 0) {
        // Trial expired - automatically convert to free tier
        return {
          ...trial,
          isActive: false,
          hasExpired: true,
          isFreeTier: true, // Automatically convert to free tier
          daysRemaining: 0,
          spellCheckUsageCount: 0,
          spellCheckDailyLimit: FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT,
          aiUsageCount: 0,
          aiDailyLimit: FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT,
          lastResetDate: new Date().toDateString()
        };
      }
      
      return {
        ...trial,
        daysRemaining,
        isActive: true,
        hasExpired: false
      };
    }
    
    return trial;
  }
  
  /**
   * Convert to free tier
   */
  static convertToFreeTier(trial: TrialStatus): TrialStatus {
    const now = new Date();
    return {
      ...trial,
      isActive: false,
      hasExpired: true,
      isFreeTier: true,
      aiUsageCount: 0,
      aiDailyLimit: FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT,
      spellCheckUsageCount: 0,
      spellCheckDailyLimit: FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT,
      lastResetDate: now.toDateString()
    };
  }
  
  /**
   * Check free tier usage and reset daily if needed
   */
  private static checkFreeTierUsage(trial: TrialStatus): TrialStatus {
    // Reset usage if day has changed
    const today = new Date().toDateString();
    const lastResetDate = trial.lastResetDate || '';
    
    if (today !== lastResetDate) {
      return {
        ...trial,
        aiUsageCount: 0,
        spellCheckUsageCount: 0,
        lastResetDate: today
      };
    }
    
    return trial;
  }
  
  /**
   * Check if AI feature can be used
   */
  static canUseAIFeature(trial?: TrialStatus, isPremium?: boolean): {
    allowed: boolean;
    reason?: 'trial' | 'premium' | 'free_tier' | 'limit_exceeded' | 'expired';
    remainingUses?: number;
  } {
    // Premium users have unlimited access
    if (isPremium) {
      return { allowed: true, reason: 'premium' };
    }
    
    if (!trial) {
      // For development, allow if no trial is set
      return { allowed: true, reason: 'trial' };
    }
    
    // Active trial state
    if (trial.isActive && !trial.hasExpired) {
      return { allowed: true, reason: 'trial' };
    }
    
    // Free tier
    if (trial.isFreeTier) {
      const usageCount = trial.aiUsageCount || 0;
      const dailyLimit = trial.aiDailyLimit || FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT;
      
      if (usageCount < dailyLimit) {
        return { 
          allowed: true, 
          reason: 'free_tier',
          remainingUses: dailyLimit - usageCount
        };
      } else {
        return { 
          allowed: false, 
          reason: 'limit_exceeded',
          remainingUses: 0
        };
      }
    }
    
    // Trial expired but not converted to free tier
    return { allowed: false, reason: 'expired' };
  }
  
  /**
   * Check if spell check can be used
   */
  static canUseSpellCheck(trial?: TrialStatus, isPremium?: boolean): {
    allowed: boolean;
    reason?: 'trial' | 'premium' | 'free_tier' | 'limit_exceeded' | 'expired';
    remainingUses?: number;
  } {
    // Premium users have unlimited access
    if (isPremium) {
      return { allowed: true, reason: 'premium' };
    }
    
    if (!trial) {
      return { allowed: false, reason: 'expired' };
    }
    
    // Active trial state
    if (trial.isActive && !trial.hasExpired) {
      return { allowed: true, reason: 'trial' };
    }
    
    // Free tier
    if (trial.isFreeTier) {
      const usageCount = trial.spellCheckUsageCount || 0;
      const dailyLimit = trial.spellCheckDailyLimit || FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT;
      
      if (usageCount < dailyLimit) {
        return { 
          allowed: true, 
          reason: 'free_tier',
          remainingUses: dailyLimit - usageCount
        };
      } else {
        return { 
          allowed: false, 
          reason: 'limit_exceeded',
          remainingUses: 0
        };
      }
    }
    
    // Trial expired but not converted to free tier
    return { allowed: false, reason: 'expired' };
  }
  
  /**
   * Increment AI feature usage
   */
  static incrementAIUsage(trial?: TrialStatus): TrialStatus | undefined {
    if (!trial) {
      return trial;
    }
    
    // Don't increment during trial period (unlimited)
    if (trial.isActive && !trial.hasExpired) {
      return trial;
    }
    
    // Only increment for free tier
    if (trial.isFreeTier) {
      return {
        ...trial,
        aiUsageCount: (trial.aiUsageCount || 0) + 1
      };
    }
    
    return trial;
  }
  
  /**
   * Increment spell check usage
   */
  static incrementSpellCheckUsage(trial?: TrialStatus): TrialStatus | undefined {
    if (!trial) {
      return trial;
    }
    
    // Don't increment during trial period (unlimited)
    if (trial.isActive && !trial.hasExpired) {
      return trial;
    }
    
    // Only increment for free tier
    if (trial.isFreeTier) {
      return {
        ...trial,
        spellCheckUsageCount: (trial.spellCheckUsageCount || 0) + 1
      };
    }
    
    return trial;
  }
  
  /**
   * Record AI usage (for trial tracking)
   */
  static recordUsage(): void {
    // In a real implementation, this would update trial usage counters
    // For now, it's a placeholder for compatibility
  }

  /**
   * Generate trial status message
   */
  static getStatusMessage(trial?: TrialStatus, isPremium?: boolean): string {
    if (isPremium) {
      return 'Premium subscription active';
    }
    
    if (!trial) {
      return 'No trial information';
    }
    
    if (trial.isActive && !trial.hasExpired) {
      return `Trial period: ${trial.daysRemaining} days remaining`;
    }
    
    if (trial.isFreeTier) {
      const aiDailyLimit = trial.aiDailyLimit || FREE_TIER_LIMITS.DAILY_AI_ANALYSIS_LIMIT;
      const spellCheckDailyLimit = trial.spellCheckDailyLimit || FREE_TIER_LIMITS.DAILY_SPELL_CHECK_LIMIT;
      const aiRemaining = aiDailyLimit - (trial.aiUsageCount || 0);
      const spellCheckRemaining = spellCheckDailyLimit - (trial.spellCheckUsageCount || 0);
      return `Free tier: Spell check ${spellCheckRemaining}/${spellCheckDailyLimit}, AI analysis ${aiRemaining}/${aiDailyLimit} remaining`;
    }
    
    return 'Trial expired';
  }
}